import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import InsulationSlider from 'components/common/insulation-slider';
import ProjectSetting from 'components/common/project-setting';
import ProjectSettingBoolean from 'components/common/project-setting-boolean';
import Button from 'components/inputs/button';
import InfoTooltip from 'components/layout/info-tooltip';
import InternalNote from 'components/layout/internal-note';
import { SizingItemsType } from 'data/types';
import {
  markDefaultCategoryModified,
  setDefaultSizingItems
} from 'features/data/data-slice';
import { resetFineTuningDefaults } from 'features/data/data-thunks';
import useDerivedDuctDefaults from 'hooks/use-derived-duct-defaults';
import useHasFineTuningDefaultChanges from 'hooks/use-has-fine-tuning-default-changes';
import { useInsulationSliderRows } from 'hooks/use-insulation-slider-rows';
import useIsDuctedPathway from 'hooks/use-is-ducted-pathway';
import useIsHybridPathway from 'hooks/use-is-hybrid-pathway';
import { useCallback, useMemo, useState } from 'react';
import { insulationRowsDifferFromRecommended } from 'utils/fine-tuning-defaults-changed';
import { SizingItemConfig } from 'utils/sizing-item-configs';
import { structureHasFlatCeilingLevel } from 'utils/structure';
import { getVisibleSizingDefaultsCategories } from 'utils/structure-sizing-presence';
import { notification } from 'utils/ui';
import { AccordionCategory, AccordionItem } from './sizing-defaults-accordion';
import SizingItemDefaultsForm from './sizing-item-defaults-form';

export default function DefaultsStep() {
  const dispatch = useAppDispatch();
  const { defaultSizingItems } = useAppSelector((state) => state.data);
  const { structure } = useAppSelector((state) => state.structure);
  const hasFlatCeilingLevel = structureHasFlatCeilingLevel(structure);
  const isDuctedPathway = useIsDuctedPathway();
  const isHybridPathway = useIsHybridPathway();
  const derivedDucts = useDerivedDuctDefaults();
  const insulationRows = useInsulationSliderRows();
  const visibleInsulationRows = insulationRows.filter(
    (row) => !row.disabledReason
  );
  const visibleSizingCategories = useMemo(
    () => getVisibleSizingDefaultsCategories(structure),
    [structure]
  );
  const [isResetting, setIsResetting] = useState(false);
  const { hasChanges: materialDefaultsDiffer, refreshRecommended } =
    useHasFineTuningDefaultChanges();
  const showResetToDefaults =
    insulationRowsDifferFromRecommended(visibleInsulationRows) ||
    materialDefaultsDiffer;

  const handleUpdate = useCallback(
    (config: SizingItemConfig, updates: Record<string, unknown>) => {
      const currentValues =
        defaultSizingItems[config.defaultsKey as keyof SizingItemsType];
      const newValues = {
        ...currentValues,
        ...updates
      };

      dispatch(
        setDefaultSizingItems({
          ...defaultSizingItems,
          [config.defaultsKey]: newValues
        })
      );

      dispatch(markDefaultCategoryModified(config.category));
    },
    [dispatch, defaultSizingItems]
  );

  const handleResetToDefaults = useCallback(async () => {
    setIsResetting(true);
    try {
      await dispatch(resetFineTuningDefaults()).unwrap();
      refreshRecommended();
    } catch {
      notification('Could not reset to recommended defaults', 'error');
    } finally {
      setIsResetting(false);
    }
  }, [dispatch, refreshRecommended]);

  return (
    <div className='flex h-full w-full flex-col gap-4 overflow-y-auto p-4'>
      <div className='flex items-center gap-3'>
        <div className='min-w-0 flex-1'>
          <InternalNote>
            Updating any properties below will modify the settings for all
            sizing items currently part of the structure. This is an optional
            step that can improve sizing accuracy.
          </InternalNote>
        </div>
        {showResetToDefaults && (
          <div className='shrink-0'>
            <Button
              color='white'
              text='Reset to defaults'
              icon={ArrowPathIcon}
              iconSize='sm'
              loading={isResetting}
              disabled={isResetting}
              tooltip='Reset insulation sliders and material defaults to the recommended values for this home'
              onClick={handleResetToDefaults}
            />
          </div>
        )}
      </div>
      {visibleInsulationRows.length > 0 && (
        <div id='structure-details-insulation' className='flex flex-col gap-3'>
          <div className='flex items-center gap-2 border-b border-gray-300 pb-2 text-lg font-bold text-gray-700'>
            Insulation Levels
            <InfoTooltip>
              <ul className='list-disc pl-4'>
                <li>
                  Use the slider to set insulation from 1 (poor) to 5
                  (excellent) for each category.
                </li>
                <li>
                  A recommended level based on year built and climate zone is
                  shown in each row&apos;s tooltip.
                </li>
                <li>
                  The slider will be grayed out and show &quot;fine-tuned
                  values&quot; if insulation has been adjusted individually. Use
                  the reset button to return to the recommended level.
                </li>
              </ul>
            </InfoTooltip>
          </div>
          <div className='flex flex-wrap gap-x-6 gap-y-3'>
            {visibleInsulationRows.map((row) => (
              <div
                key={row.key}
                className='flex max-w-md grow basis-[280px] flex-col gap-1'
              >
                <span className='flex items-center gap-1 text-sm font-semibold text-gray-700'>
                  {row.label}
                  <InfoTooltip>
                    {row.tooltip}
                    {row.suggestedLevel && (
                      <>
                        <br />
                        <br />
                        Recommendation: Level {row.suggestedLevel}
                        <br />
                        (Based on year built and climate zone)
                      </>
                    )}
                  </InfoTooltip>
                </span>
                <InsulationSlider
                  value={row.value}
                  suggestedLevel={row.suggestedLevel}
                  onChange={row.onChange}
                  levelTooltips={row.levelTooltips}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {visibleSizingCategories.map((category) => (
        <AccordionCategory
          key={category.id}
          title={category.title}
          description={category.description}
          defaultOpen={false}
          id={`${category.id}-default-settings`}
        >
          {category.items.map((itemConfig) => (
            <AccordionItem
              key={itemConfig.id}
              title={itemConfig.title}
              defaultOpen={false}
            >
              <SizingItemDefaultsForm
                config={itemConfig}
                currentValues={
                  defaultSizingItems[
                    itemConfig.defaultsKey as keyof SizingItemsType
                  ] as Record<string, unknown>
                }
                onUpdate={(updates) => handleUpdate(itemConfig, updates)}
              />
            </AccordionItem>
          ))}
        </AccordionCategory>
      ))}
      {(isDuctedPathway || isHybridPathway) && (
        <AccordionCategory
          id='ducted-settings'
          title='Ducted System Settings'
          description=''
          defaultOpen={false}
        >
          <AccordionItem title='Discount Blower Heat Gain' defaultOpen={false}>
            <div className='flex flex-col gap-2'>
              <p className='text-sm text-gray-600'>
                Set to &quot;No&quot; for ducted systems with the air handler in
                unconditioned space (attic, garage, vented crawl) - Manual J
                then adds the central blower&apos;s motor heat (&lt;1,700 BTU/h)
                back to the cooling load.
              </p>
              <ProjectSettingBoolean
                code='has_blower_heat_discount'
                showLabel
              />
            </div>
          </AccordionItem>
          <AccordionItem title='Duct Location' defaultOpen={false}>
            <div className='flex flex-col gap-2'>
              <p className='text-sm text-gray-600'>
                Where the supply and return ducts run. Ducts in unconditioned
                space (attic, crawlspace) add significant heat loss/gain. Until
                changed, the default is estimated from equipment placement and
                per-level ductwork answers.
              </p>
              <ProjectSetting
                code='duct_system_type'
                fallbackValue={derivedDucts.ductSystemType}
                showLabel
              />
            </div>
          </AccordionItem>
          <AccordionItem title='Duct Insulation' defaultOpen={false}>
            <div className='flex flex-col gap-2'>
              <p className='text-sm text-gray-600'>
                Insulation R-value of the duct walls. Higher values reduce duct
                heat loss/gain. R-4 is typical for existing homes.
              </p>
              <ProjectSetting
                code='duct_wall_insulation'
                fallbackValue={derivedDucts.ductWallInsulation}
                showLabel
              />
            </div>
          </AccordionItem>
          <AccordionItem title='Duct Leakage' defaultOpen={false}>
            <div className='flex flex-col gap-2'>
              <p className='text-sm text-gray-600'>
                Duct leakage class (supply / return). Sealed new ductwork is
                around 0.12 / 0.24; typical unsealed existing ducts are around
                0.24 / 0.47.
              </p>
              <ProjectSetting
                code='duct_leakage_class'
                fallbackValue={derivedDucts.ductLeakageClass}
                showLabel
              />
            </div>
          </AccordionItem>
        </AccordionCategory>
      )}
      {hasFlatCeilingLevel && (
        <AccordionCategory
          id='joist-spacing-settings'
          title='Joist Spacing'
          description=''
          defaultOpen={false}
        >
          <AccordionItem title='Joist width' defaultOpen={false}>
            <ProjectSetting code='joist_width' />
          </AccordionItem>
        </AccordionCategory>
      )}
      <AccordionCategory
        id='year-built-settings'
        title='Additional Attributes'
        description=''
        defaultOpen={false}
      >
        <div className='flex max-w-[240px] flex-col gap-1'>
          <span className='text-sm font-semibold text-gray-700'>
            Year Built or Renovated
          </span>
          <ProjectSetting code='year_built' />
          <p className='text-xs text-gray-500'>
            Used to recommend insulation levels based on age and climate zone.
          </p>
        </div>
      </AccordionCategory>
    </div>
  );
}
